/**
 * src/features/account/masking.js
 *
 * Client-side PII masking utilities.
 *
 * These display masks are applied in the UI for shoulder-surf protection.
 * The real values are NEVER returned by the default GET /api/users/me
 * endpoint — the server sends already-masked values.  This file is a
 * second layer of safety and provides consistent formatting.
 *
 * Masking rules (spec):
 *  email     "jo****@gm***.com"
 *             keep first 2 chars of local part, mask the rest
 *             keep first 2 chars of domain name, mask the rest, keep TLD
 *
 *  phone     "+63 *** *** 1234"
 *             keep country code (or first 2 chars) and last 4 digits
 *
 *  birthdate "****-**-**"
 *             always fully masked (year included)
 */

export const SENSITIVE_KEYS = /** @type {const} */ (["email", "phone", "birthdate"]);

/**
 * Mask an email address.
 *
 * "jhoanna@gmail.com"  →  "jh*****@gm***.com"
 * "ab@cd.io"           →  "ab@cd***.io"  (local ≤2 → keep all)
 */
export function maskEmail(email) {
  const v = String(email || "").trim();
  if (!v) return "";
  if (!v.includes("@")) return "***";

  const atIdx = v.lastIndexOf("@");
  const local = v.slice(0, atIdx);
  const domainFull = v.slice(atIdx + 1);

  // mask local: keep first 2 chars, replace the rest with *
  const maskedLocal =
    local.length <= 2
      ? local + "*".repeat(Math.max(1, 4 - local.length))
      : local.slice(0, 2) + "*".repeat(local.length - 2);

  // split domain name from TLD (last dot)
  const dotIdx = domainFull.lastIndexOf(".");
  const domainName = dotIdx > 0 ? domainFull.slice(0, dotIdx) : domainFull;
  const tld = dotIdx > 0 ? domainFull.slice(dotIdx) : "";

  // mask domain name: keep first 2 chars
  const maskedDomain =
    domainName.length <= 2
      ? domainName + "*".repeat(Math.max(1, 3 - domainName.length))
      : domainName.slice(0, 2) + "*".repeat(domainName.length - 2);

  return `${maskedLocal}@${maskedDomain}${tld}`;
}

/**
 * Mask a phone number.
 *
 * "+639123456789"  →  "+63 *** *** 6789"
 * "09123456789"    →  "09 *** *** 6789"
 */
export function maskPhone(phone) {
  const v = String(phone || "").trim().replace(/[\s\-().]/g, "");
  if (!v) return "";
  if (v.length < 4) return "***";

  const last4 = v.slice(-4);

  let prefix = "";
  if (v.startsWith("+63")) {
    prefix = "+63";
  } else if (v.startsWith("09")) {
    prefix = "09";
  } else if (v.startsWith("+")) {
    const m = v.match(/^(\+\d{1,4})/);
    prefix = m ? m[1] : v.slice(0, 3);
  } else {
    prefix = v.slice(0, 2);
  }

  return `${prefix} *** *** ${last4}`;
}

/**
 * Mask a birthdate — always returns "****-**-**" regardless of value.
 * The server already returns masked strings; this is a UI safety fallback.
 */
export function maskBirthdate() {
  return "**-**-****";
}

/**
 * Apply the appropriate mask for a profile field key.
 * Returns the masked string, or the original value if the key is not sensitive.
 */
export function maskField(key, value) {
  if (!value) return "";
  if (key === "email") return maskEmail(value);
  if (key === "phone") return maskPhone(value);
  if (key === "birthdate") return maskBirthdate();
  return String(value);
}

/**
 * Returns true when the key is a masked (sensitive) field.
 */
export function isSensitive(key) {
  return SENSITIVE_KEYS.includes(key);
}
